// Corp scoring grids from the client's scoring matrices. Corp deals up to $50k
// (micro-ticket) score on their own point table; larger corp deals use the
// standard table. Athena handles deals up to $500k, and its model scores only
// deals up to $250k.

export const MICRO_TICKET_MAX = 50000;
export const MODEL_SCORING_MAX = 250000;
export const LOAN_AMOUNT_MAX = 500000;

export const isMicroTicket = (loanAmount: unknown): boolean => {
  const amount = Number(loanAmount) || 0;
  return amount > 0 && amount <= MICRO_TICKET_MAX;
};

// LeaseWorks bank-statement criteria (yes / no, from the pipeline): three months of
// statements showing the minimum average balance and annualized deposits. The
// first pair applies to corp deals over $250k, the second to deals over $350k.
export const BANK_STATEMENT_FIELDS = [
  { field: "udf_min_100k_avg_bal_3mo", label: "Min $100K Avg Balance (3 Mo)" },
  { field: "udf_min_annual_dep_5_million", label: "Min $5M Annualized Deposits" },
  { field: "udf_min_250k_avg_bal_3mo", label: "Min $250K Avg Balance (3 Mo)" },
  { field: "udf_min_annual_dep_10_million", label: "Min $10M Annualized Deposits" },
];

// The pair a corp deal must meet; null when the loan is $250k or less.
export const bankStatementCriteria = (loanAmount: unknown) => {
  const amount = Number(loanAmount) || 0;
  if (amount > 350000) return BANK_STATEMENT_FIELDS.slice(2);
  if (amount > MODEL_SCORING_MAX) return BANK_STATEMENT_FIELDS.slice(0, 2);
  return null;
};

// LeaseWorks sends "yes" / "no", read in any case; anything else, NULL included,
// means nothing was entered.
export const yesNo = (value: unknown): "Yes" | "No" | "" => {
  const text = String(value ?? "").trim().toLowerCase();
  return text === "yes" ? "Yes" : text === "no" ? "No" : "";
};

// Values and points for the corp categories derived from application data.
// Micro-ticket deals take the higher of the D&B and PayNet figures for high
// credit and trade lines, and score high credit as a share of the loan amount.
export function corpGridFields(values: Record<string, any>) {
  const micro = isMicroTicket(values.loanAmount);
  const years = Number(values.timeInBusiness) || 0;
  const employees = Number(values.numberOfEmployees) || 0;
  const highCredit = micro
    ? Math.max(Number(values.highCredit) || 0, Number(values.pnHighCredit) || 0)
    : values.highCredit;
  const tradeLines = micro
    ? Math.max(Number(values.noOfTradeExperiences) || 0, Number(values.pnNoOfTradeExperiences) || 0)
    : values.noOfTradeExperiences;
  const credit = Number(highCredit) || 0;
  const lines = Number(tradeLines) || 0;

  if (micro) {
    const share = credit / Number(values.loanAmount);
    return {
      corpTimeInBusiness: values.timeInBusiness,
      corpTimeInBusinessPoints: years > 15 ? 3 : years > 10 ? 2 : years >= 4 ? 1 : 0,
      corpNumberOfEmployees: values.numberOfEmployees,
      corpNumberOfEmployeesPoints: employees >= 50 ? 3 : employees >= 25 ? 2 : employees >= 10 ? 1 : 0,
      corpHighCredit: highCredit,
      corpHighCreditPoints: share >= 0.75 ? 3 : share >= 0.5 ? 2 : share >= 0.25 ? 1 : 0,
      corpTradeLines: tradeLines,
      corpTradeLinesPoints: lines >= 10 ? 2 : lines >= 5 ? 1 : 0,
    };
  }
  return {
    corpTimeInBusiness: values.timeInBusiness,
    corpTimeInBusinessPoints: years > 5 ? 2 : years >= 4 ? 1 : 0,
    corpNumberOfEmployees: values.numberOfEmployees,
    corpNumberOfEmployeesPoints: employees >= 20 ? 2 : employees >= 10 ? 1 : 0,
    corpHighCredit: highCredit,
    corpHighCreditPoints: credit >= 10000 ? 2 : credit >= 2500 ? 1 : 0,
    corpTradeLines: tradeLines,
    corpTradeLinesPoints: lines >= 10 ? 2 : lines >= 5 ? 1 : 0,
  };
}
